import React from "react";
import { connect } from "react-redux";
import _ from "underscore";
import PropTypes from "prop-types";
import ActionLogger from "action-logger";
import AvailabilityFilterHelpers from "helpers/availability-filter-helpers";
import Constants from "rtr-constants";
import { browserPropType, gridViewPropType, membershipStatePropType } from "components/propTypes";
import { ZIP_CODE_MODAL_OBJECT_TYPE } from "helpers/dfc-ux-helper";
import GridTopFiltersAvailability from "./grid-top-filters-availability";
import GridSuperBarTopFiltersAvailability from "./grid-superbar-top-filters-availability";
import GridTopFiltersDate from "./grid-top-filters-date";
import GridTopFiltersEvent from "./grid-top-filters-event";
import GridTopFiltersZip from "./grid-top-filters-zip";
import GridTopFiltersSizeAndMaternity from "./grid-top-filters-size-and-maternity";
import { PanelNames, ZipPanelNames, SizePanelNames } from "helpers/grid-top-filters-helpers";
import MaternityHelper from "helpers/maternity-helper";
import MembershipHelpers from "helpers/membership-helpers";
import MultiPanelModal from "./multi-panel-modal";
import productsActions from "actions/products-actions";
import sharedActions from "actions/shared-actions";
import deliveryCutoffActions from "actions/delivery-cutoff-actions";
import AvailabilityPreferencesModalHelpers from "helpers/availability-preferences-modal-helpers";
import RentalOptionIntent, { ActionsTaken, RentalOptions } from "helpers/rental-option-intent-helper";
import { compose } from "redux";
import { withPersistedFilters } from "../hoc/with-persisted-filters";

const CONSTANTS_MEMBERSHIPLENS = Constants.membershipLens;
const CONSTANTS_MEMBERSHIPLENS_CLASSIC = CONSTANTS_MEMBERSHIPLENS.classic;
const CONSTANTS_MEMBERSHIPLENS_RTRUPDATE = CONSTANTS_MEMBERSHIPLENS.RTRUpdate;
const CONSTANTS_MEMBERSHIPLENS_UNLIMITED = CONSTANTS_MEMBERSHIPLENS.unlimited;
const CONSTANTS_AVAILABILITY = Constants.availabilityFilter;
const loggingObjectType = Constants.analytics.OBJECT_TYPE.GRID;

const AVAILABLE_NOW_AVAILABILITY = 1;
const ALL_STYLES_AVAILABILITY = 0;

const filterModalName = "grid-filter-modal";

const {
  unlimitedMinAvailabilityKey: unlimitedMinAvailabilityFilterKey,
  rtrUpdateMinAvailabilityFilterKey,
  allAvailableFilterValue,
  allStylesAvailabilityFilterValue,
} = AvailabilityFilterHelpers;

export class GridFilterMultiPanelModal extends React.Component {
  static propTypes = {
    browser: browserPropType,
    changeFilters: PropTypes.func.isRequired,
    clearPreferences: PropTypes.func.isRequired,
    dateOptions: PropTypes.shape({
      blackoutDays: PropTypes.arrayOf(PropTypes.string).isRequired,
      dateEnd: PropTypes.string.isRequired,
      dateStart: PropTypes.string.isRequired,
    }).isRequired,
    dispatch: PropTypes.func.isRequired,
    displayedModal: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
    gridView: gridViewPropType.isRequired,
    membershipState: membershipStatePropType,
    message: PropTypes.string,
    messageTwo: PropTypes.string,
    setMembershipLens: PropTypes.func,
    submitFilters: PropTypes.func,
    userData: PropTypes.shape({
      isUnlimitedLens: PropTypes.bool,
      isRTRUpdateLens: PropTypes.bool,
    }).isRequired,
    workingFilters: PropTypes.shape({
      canonicalSizes: PropTypes.array,
      zip_code: PropTypes.string,
    }).isRequired,
    writePreferences: PropTypes.func.isRequired,
  };

  static defaultProps = {
    gridView: {
      isUnlimited: false,
      isRtrUpdate: false,
    },
  };

  static filterModalName = filterModalName;

  state = {
    currentPanel: this.getInitialPanel(),
    filtersToApply: {},
    panelKeys: [this.getInitialPanel()],
    selectedMinAvailability: this.getInitialSelectedMinAvailability(),
    width: this.getWidth(this.getInitialPanel()),
    progressBarSteps: 0,
  };

  componentDidMount() {
    const zipFilter = this.props?.workingFilters?.zip_code ?? "";
    if (!_.isEmpty(zipFilter)) {
      this.logModalOpen(zipFilter);
    }
  }

  componentDidUpdate(prevProps) {
    const oldZipFilter = prevProps?.workingFilters?.zip_code ?? "";
    const newZipFilter = this.props?.workingFilters?.zip_code ?? "";

    if (_.isEmpty(oldZipFilter) && !_.isEmpty(newZipFilter)) {
      this.logModalOpen(newZipFilter);
    }
  }

  logModalOpen(zipCode) {
    if (this.props.displayedModal !== filterModalName) {
      return;
    }
    const lens = MembershipHelpers.getUserDataLens(this.props.userData);

    ActionLogger.logAction({
      object_type: ZIP_CODE_MODAL_OBJECT_TYPE,
      action: "zip_code_modal_displayed",
      lens: lens,
      zip: zipCode,
    });
  }

  getInitialPanel() {
    return PanelNames.SUPERBAR_AVAILABILITY;
  }

  getInitialSelectedMinAvailability() {
    return AvailabilityFilterHelpers.membershipMinAvailability(this.props.userData, this.props.workingFilters)
      ? allAvailableFilterValue
      : allStylesAvailabilityFilterValue;
  }

  onClickCancelEventPanel = () => {
    this.closeModal();
    this.applyFilters();

    ActionLogger.logAction({
      object_type: ZIP_CODE_MODAL_OBJECT_TYPE,
      action: "just_browsing",
    });
  };

  closeModal = () => {
    this.props.dispatch(sharedActions.displayModal(false));
  };

  getWidth(panelName) {
    if (_.includes(ZipPanelNames, panelName)) {
      return (1 / 3) * 100;
    }
    if (_.includes(SizePanelNames, panelName)) {
      return (2 / 3) * 100;
    }
    return 100;
  }

  loadNextPanel(nextPanel) {
    const newPanelKeys = this.state.panelKeys.concat(nextPanel);
    const newWidth = this.getWidth(nextPanel);

    this.setState({
      currentPanel: nextPanel,
      panelKeys: newPanelKeys,
      width: newWidth,
    });
  }

  loadPreviousPanel = previousPanel => {
    const newPanelKeys = this.state.panelKeys.slice(0);
    const index = newPanelKeys.indexOf(previousPanel);
    newPanelKeys.splice(index + 1);
    const newCurrentPanel = newPanelKeys[newPanelKeys.length - 1];
    this.setState({
      currentPanel: newCurrentPanel,
      panelKeys: newPanelKeys,
    });
  };

  logZipConfirmed(zipCode) {
    const lens = MembershipHelpers.getUserDataLens(this.props.userData);

    ActionLogger.logAction({
      object_type: ZIP_CODE_MODAL_OBJECT_TYPE,
      action: "zip_code_click_confirm",
      lens: lens,
      zip: zipCode,
    });
  }

  logSizeSaved(sizes) {
    const inMaternityExperience = MaternityHelper.inMaternityExperience(this.props.userData);

    ActionLogger.logAction({
      object_type: ZIP_CODE_MODAL_OBJECT_TYPE,
      action: "size_select",
      maternity: inMaternityExperience,
      itemSizes: sizes.join(","),
    });
  }

  logDateSaved(date) {
    ActionLogger.logAction({
      object_type: ZIP_CODE_MODAL_OBJECT_TYPE,
      action: "rezo_select",
      rent_begin_date: date,
    });
  }

  logModalClose(panelName) {
    ActionLogger.logAction({
      object_type: ZIP_CODE_MODAL_OBJECT_TYPE,
      action: "zip_code_modal_closed",
      panel_name: panelName,
    });
  }

  onClickZipCta = zipCodeFilter => {
    this.persistFilters(zipCodeFilter);
    this.saveFiltersToSubmit(zipCodeFilter);
    this.updateWorkingFilters(zipCodeFilter);
    this.updateBlackoutDays(zipCodeFilter.zip_code);
    this.logZipConfirmed(zipCodeFilter.zip_code);
    this.applyFilters(zipCodeFilter);
    this.closeModal();
  };

  onClickSizeCta = sizeFilters => {
    this.persistFilters(sizeFilters);
    this.saveFiltersToSubmit(sizeFilters);
    this.loadNextPanel(PanelNames.ZIP);
    this.logSizeSaved(sizeFilters.canonicalSizes);
  };

  onClickMaternityText = () => {
    const inMaternityExperience = MaternityHelper.inMaternityExperience(this.props.userData);
    if (inMaternityExperience) {
      return;
    }

    ActionLogger.logAction({
      object_type: ZIP_CODE_MODAL_OBJECT_TYPE,
      action: "enter_maternity_flow",
    });
  };

  onClickOptOut() {
    ActionLogger.logAction({
      object_type: ZIP_CODE_MODAL_OBJECT_TYPE,
      action: "opt_out_maternity_flow",
    });
  }

  onClickSaveDueDate(dueDate) {
    ActionLogger.logAction({
      object_type: ZIP_CODE_MODAL_OBJECT_TYPE,
      action: "submit_due_date",
      due_date: dueDate,
    });
  }

  onClickContinueEventPanel = () => {
    this.loadNextPanel(PanelNames.DATE);
    this.props.dispatch(productsActions.setMembershipLens(CONSTANTS_MEMBERSHIPLENS_CLASSIC));

    ActionLogger.logAction({
      object_type: ZIP_CODE_MODAL_OBJECT_TYPE,
      action: "rezo_search",
    });
  };

  onClickDateCta = dateFilters => {
    const date = dateFilters?.date ?? "";
    this.logDateSaved(date);

    if (!dateFilters?.date) {
      this.removeDateFilters();
    } else {
      this.persistFilters(dateFilters);
      this.applyFilters(dateFilters);
    }

    this.loadNextPanel(PanelNames.SIZE);
  };

  onChangeSubscriptionAvailability = filter => {
    this.setState({
      selectedMinAvailability: filter,
    });
  };

  onClickAvailabilitySubmit = () => {
    const { selectedMinAvailability } = this.state;
    const { isUnlimited } = this.props.gridView;

    // minAvailableFilterGroupKey is either unlimitedMinAvailabilityKey or rtrUpdateMinAvailabilityFilterKey
    const minAvailableFilterGroupKey = isUnlimited
      ? unlimitedMinAvailabilityFilterKey
      : rtrUpdateMinAvailabilityFilterKey;

    const filterData = { [minAvailableFilterGroupKey]: selectedMinAvailability };
    this.persistFilters(filterData);
    this.applyFilters(filterData);
    this.closeModal();
  };

  onClickSuperAvailabilitySubmit = filter => {
    const hasRtrUpdateInventory = MembershipHelpers.hasInventoryEligibility(
      this.props.membershipState,
      Constants.inventoryEligibilities.rtr_update
    );

    // The only group with rtr update availability is the 1-swap user.
    // All other fixed swap and non subs will have rtr unlimted availability.
    const minAvailableFilterGroupKey = hasRtrUpdateInventory
      ? rtrUpdateMinAvailabilityFilterKey
      : unlimitedMinAvailabilityFilterKey;

    const selectedMembershipLens = hasRtrUpdateInventory
      ? CONSTANTS_MEMBERSHIPLENS_RTRUPDATE
      : CONSTANTS_MEMBERSHIPLENS_UNLIMITED;

    const membershipAvailabilityFilterData = {
      [minAvailableFilterGroupKey]: this.currentMembershipAvailabilityView(filter),
    };

    if (filter === CONSTANTS_AVAILABILITY.specificDates) {
      this.setState({ progressBarSteps: 3 });
      this.loadNextPanel(PanelNames.DATE);
    } else {
      // RL 5/21 - updated working filters don't need to be submitted using the submitFilters action
      // because the setMembershipLens action does the same thing (redirects to a url with the new filter query params),
      // but including the updated membership lens
      this.persistFilters(membershipAvailabilityFilterData);
      this.changeMembershipFilters(membershipAvailabilityFilterData);
      this.logSubmitAvailability(membershipAvailabilityFilterData);
      this.props.setMembershipLens(selectedMembershipLens);
      this.setIntent(selectedMembershipLens);

      if (filter === CONSTANTS_AVAILABILITY.allStyles) {
        this.closeModal();
      } else {
        this.setState({ progressBarSteps: 2 });
        this.loadNextPanel(PanelNames.SIZE);
      }
    }
  };

  persistFilters(filters) {
    const newFilters = {
      ...this.props.workingFilters,
      ...filters,
    };
    this.props.writePreferences(newFilters);
  }

  changeMembershipFilters(filters) {
    const availabilityFilters = { ...this.props.workingFilters, ...filters };

    // When viewing membership availability we don't want to include date and duration filters
    const membershipFilters = _.omit(availabilityFilters, ["date", "duration"]);

    this.props.changeFilters(membershipFilters);
  }

  currentMembershipAvailabilityView(filter) {
    return filter === CONSTANTS_AVAILABILITY.shipsDefault ? AVAILABLE_NOW_AVAILABILITY : ALL_STYLES_AVAILABILITY;
  }

  logSubmitAvailability(selectedFilters) {
    const props = { ...this.props, analyticsObjectType: loggingObjectType, analyticsOpenedFrom: "grid" };
    AvailabilityPreferencesModalHelpers.logFilterAction(props, "explicit", selectedFilters);
  }

  // This will determine what radio button on the non-sub PDP is selected
  setIntent(membershipName) {
    const unlimitedIntent = RentalOptions.NONE;
    const rtrUpdateIntent = RentalOptions.NONE;

    switch (membershipName) {
      case Constants.membershipLens.classic:
        RentalOptionIntent.setLastIntent(RentalOptions.RESERVE, ActionsTaken.RESERVE_SUPERBAR, "grid");
        break;
      case Constants.membershipLens.unlimited:
        RentalOptionIntent.setLastIntent(unlimitedIntent, ActionsTaken.UNLIMITED_PREVIEW_SUPERBAR, "grid");
        break;
      case Constants.membershipLens.RTRUpdate:
        RentalOptionIntent.setLastIntent(rtrUpdateIntent, ActionsTaken.UPDATE_PREVIEW_SUPERBAR, "grid");
        break;
    }
  }

  updateWorkingFilters(filters) {
    const newFilters = { ...this.props.workingFilters, ...filters };
    this.props.dispatch(productsActions.changeFilters(newFilters));
  }

  updateBlackoutDays(zipCode) {
    this.props.dispatch(deliveryCutoffActions.fetchBlackoutDays(zipCode));
  }

  saveFiltersToSubmit(filters) {
    const newFilters = _.extend({}, this.state.filtersToApply, filters);
    this.setState({
      filtersToApply: newFilters,
    });
  }

  applyFilters(extraFilter) {
    const newFilters = {
      ...this.props.workingFilters,
      ...this.state.filtersToApply,
      ...extraFilter,
    };

    if (_.isEqual(this.props.workingFilters, newFilters)) {
      return;
    }

    this.props.dispatch(productsActions.changeFilters(newFilters));
    this.props.submitFilters();
  }

  removeDateFilters() {
    const dateFilterKeys = ["date", "duration"];
    this.props.clearPreferences(dateFilterKeys);
    this.props.dispatch(productsActions.clearFilters(dateFilterKeys));
  }

  filterPanels() {
    const { workingFilters, userData, browser, dateOptions, message, messageTwo } = this.props;
    const zipHedText = "Where will we be shipping?";
    const zipDekText = "We’ll show you available styles in that location.";
    const zipMobileDekText = "We’ll show you available styles in that location.";
    const sizeHedText = "What are your go-to sizes?";
    const sizeDekText = "Select a few that fit you the best.";
    const dateHedText = "When do you need it?";

    const filterPanels = {
      [PanelNames.ZIP]: {
        key: PanelNames.ZIP,
        hed: { text: zipHedText },
        dek: {
          text: zipDekText,
          mobileText: zipMobileDekText,
        },
        className: "grid-superbar",
        content: (
          <GridTopFiltersZip
            onSubmit={this.onClickZipCta}
            workingFilters={workingFilters}
            zipCode={workingFilters?.zip_code}
            ctaText="See Available Styles"
          />
        ),
      },
      [PanelNames.SIZE]: {
        key: PanelNames.SIZE,
        content: (
          <GridTopFiltersSizeAndMaternity
            defaultSizeHedText={sizeHedText}
            defaultSizeDekText={sizeDekText}
            onClickMaternityText={this.onClickMaternityText}
            onClickOptOut={this.onClickOptOut}
            onClickSave={this.onClickSaveDueDate}
            onSubmit={this.onClickSizeCta}
            userData={userData}
            workingFilters={workingFilters}
            ctaText="Continue"
          />
        ),
      },
      [PanelNames.EVENT]: {
        key: PanelNames.EVENT,
        hed: { text: "Are you renting for a specific date?" },
        dek: { text: "We will filter your choices by date availability." },
        content: (
          <GridTopFiltersEvent
            browser={browser}
            onClickCancel={this.onClickCancelEventPanel}
            onClickContinue={this.onClickContinueEventPanel}
          />
        ),
      },
      [PanelNames.DATE]: {
        key: PanelNames.DATE,
        content: (
          <GridTopFiltersDate
            browser={browser}
            dateOptions={dateOptions}
            hedText={dateHedText}
            onChange={_.noop}
            onClickBrowse={this.onClickDateCta}
            onClickClear={_.noop}
            workingFilters={workingFilters}
            message={message}
            messageTwo={messageTwo}
            ctaText="Continue"
          />
        ),
      },
      [PanelNames.AVAILABILITY]: {
        key: PanelNames.AVAILABILITY,
        content: (
          <GridTopFiltersAvailability
            hedText="Select Availability"
            membershipState={this.props.membershipState}
            onChange={this.onChangeSubscriptionAvailability}
            onClickBrowse={this.onClickAvailabilitySubmit}
            userData={userData}
            workingFilters={workingFilters}
          />
        ),
      },
      [PanelNames.SUPERBAR_AVAILABILITY]: {
        key: PanelNames.SUPERBAR_AVAILABILITY,
        content: (
          <GridSuperBarTopFiltersAvailability
            dekText="Answer a few questions for the best browsing experience."
            isSubscriber={MembershipHelpers.isActiveMembership(this.props.membershipState)}
            onClickBrowse={this.onClickSuperAvailabilitySubmit}
          />
        ),
      },
    };

    return filterPanels;
  }

  getPanels() {
    const { panelKeys } = this.state;
    const filterPanels = this.filterPanels();

    return _.pick(filterPanels, panelKeys);
  }

  onRequestClose = () => {
    const panelName = this.state.currentPanel;
    this.applyFilters();
    this.logModalClose(panelName);
  };

  render() {
    const modalClassName = `${filterModalName} ${this.state.currentPanel}-panel`;
    return (
      <MultiPanelModal
        browser={this.props.browser}
        className={modalClassName}
        currentPanel={this.state.currentPanel}
        dispatch={this.props.dispatch}
        displayedModal={this.props.displayedModal}
        modalName={filterModalName}
        isBottomStickyMobile={true}
        onRequestClose={this.onRequestClose}
        panels={this.getPanels()}
        showProgressBar={true}
        translation={this.state.panelKeys.length - 1}
        width={this.state.width}
        onClickBackButton={this.loadPreviousPanel}
        panelKeys={this.state.panelKeys}
        progressBarSteps={this.state.progressBarSteps}
      />
    );
  }
}

const mapDispatchToProps = {
  changeFilters: productsActions.changeFilters,
  setMembershipLens: productsActions.setMembershipLens,
};

export default compose(withPersistedFilters, connect(null, mapDispatchToProps))(GridFilterMultiPanelModal);
